import { Node } from '@tiptap/core';
import { blockNodeSpec, dataAttribute } from './block';
import { blockViews, initialView } from './block_views';

// Quote: a pull-quote with an optional speaker and a timestamp into the post's episode. Saved as a bare
// block placeholder with data-quote-text / -speaker / -timestamp (seconds); Newsletters::RenderBlocks
// renders it, and the episode is always the post's own, never an attribute. The editor chrome lives in
// dashboard/shared/tiptap/_quote_editor.html.erb and its preview comes from the same server component.
// The block shows one view at a time (block_views.js): the suggestions, the editor, or the preview. A quote
// with text opens in its preview, a new one in the suggestions when there are any, else in the editor.
//
// The page passes data-tiptap-block-capabilities-value.quote = { post_id, episode, episode_url, duration,
// suggestions }. `episode` turns the timestamp field on; `suggestions` (the transcript's quotes, only
// present when AI blocks are on and the transcript has been read) fills the purple "Suggested quotes" box.
// A quote without an episode is still a fine pull-quote, it just has no timestamp.

const PREVIEW_DELAY_MS = 300;

// Seconds from "90", "1:30" or "1:02:03"; null when it isn't a time. Mirrors
// Block::Newsletter::QuoteComponent.parse_timestamp.
export function parseTimestamp(value) {
  const raw = (value || '').trim();
  if (!raw) return null;
  if (/^\d{1,6}$/.test(raw)) return parseInt(raw, 10);

  const parts = raw.split(':');
  if (parts.length < 2 || parts.length > 3 || !parts.every(part => /^\d{1,2}$/.test(part))) return null;

  const numbers = parts.map(part => parseInt(part, 10));
  if (numbers.slice(1).some(n => n > 59)) return null;
  return numbers.reduce((total, n) => total * 60 + n, 0);
}

// "32:14", or "1:02:03" past the hour. Mirrors ChaptersComponent.time_label_for without the zero padding
// of the minutes on short times, which reads better in a text field.
export function formatTimestamp(seconds) {
  const total = Math.floor(Number(seconds));
  if (!Number.isFinite(total) || total < 0) return '';

  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  const pad = n => String(n).padStart(2, '0');
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(secs)}` : `${minutes}:${pad(secs)}`;
}

const FIELDS = {
  text: 'quoteText',
  speaker: 'quoteSpeaker',
};

export const Quote = Node.create({
  name: 'quote',

  ...blockNodeSpec('quote', {
    attributes: {
      quoteText: dataAttribute('quoteText'),
      quoteSpeaker: dataAttribute('quoteSpeaker'),
      quoteTimestamp: dataAttribute('quoteTimestamp'),
    },
  }),

  addOptions() {
    return {
      templates: null,         // { editor_html } — injected by the tiptap controller from the server template
      capabilities: null,      // { post_id, episode, episode_url, duration, suggestions } — injected by the tiptap controller
      fetchRenderedHtml: null, // async (attrs) => string | null — injected by the tiptap controller
      onRenderedHtmlFetched: null,
    };
  },

  addNodeView() {
    return ({ node, getPos, editor }) => {
      const extension = this;
      const capabilities = extension.options.capabilities || {};
      let currentNode = node;
      let previewTimer = null;
      let previewRequest = 0;

      const dom = document.createElement('div');
      dom.setAttribute('data-quote-node', '');
      dom.innerHTML = extension.options.templates?.editor_html
        || '<div class="my-3 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-500">Quote</div>';

      const field = name => dom.querySelector(`[data-field="${name}"]`);
      const preview = dom.querySelector('[data-preview]');
      const hint = dom.querySelector('[data-preview-hint]');
      const timestampError = dom.querySelector('[data-timestamp-error]');
      const checkLink = dom.querySelector('[data-check]');

      const setAttrs = attrs => {
        const pos = typeof getPos === 'function' ? getPos() : null;
        if (pos === null || pos === undefined) return;
        editor.view.dispatch(editor.state.tr.setNodeMarkup(pos, undefined, { ...currentNode.attrs, ...attrs }));
      };

      // True while the timestamp input holds text we refused to save; syncFromNode leaves it (and its error) alone.
      let timestampInvalid = false;

      const showTimestampError = message => {
        timestampInvalid = Boolean(message);
        if (!timestampError) return;
        timestampError.textContent = message || '';
        timestampError.classList.toggle('hidden', !message);
      };

      const syncCheckLink = () => {
        if (!checkLink) return;
        const seconds = parseTimestamp(currentNode.attrs.quoteTimestamp);
        if (!capabilities.episode_url || seconds === null) {
          checkLink.classList.add('hidden');
          return;
        }
        const url = new URL(capabilities.episode_url, window.location.origin);
        url.searchParams.set('timestamp', seconds);
        checkLink.setAttribute('href', url.toString());
        checkLink.classList.remove('hidden');
      };

      const syncFromNode = () => {
        Object.entries(FIELDS).forEach(([name, attr]) => {
          const el = field(name);
          if (el && el !== document.activeElement) el.value = currentNode.attrs[attr] ?? '';
        });
        const stamp = field('timestamp');
        if (stamp && stamp !== document.activeElement && !timestampInvalid) {
          const seconds = parseTimestamp(currentNode.attrs.quoteTimestamp);
          stamp.value = seconds === null ? '' : formatTimestamp(seconds);
        }
        syncCheckLink();
        views?.refresh();
      };

      const showPreview = html => {
        if (!preview) return;
        preview.innerHTML = html || '';
        hint?.classList.toggle('hidden', Boolean(html));
      };

      const schedulePreview = () => {
        if (!extension.options.fetchRenderedHtml || views?.view !== 'preview') return;
        clearTimeout(previewTimer);
        previewTimer = setTimeout(async () => {
          const request = ++previewRequest;
          const html = await extension.options.fetchRenderedHtml({ ...currentNode.attrs, postId: capabilities.post_id || '' });
          if (request === previewRequest) showPreview(html);
        }, PREVIEW_DELAY_MS);
      };

      Object.entries(FIELDS).forEach(([name, attr]) => {
        field(name)?.addEventListener('input', e => setAttrs({ [attr]: e.target.value }));
      });

      // The timestamp is typed as mm:ss or h:mm:ss and saved as seconds. A value that isn't a time, or is
      // past the end of the episode, is flagged and not saved.
      field('timestamp')?.addEventListener('input', e => {
        const text = e.target.value.trim();
        if (text === '') {
          showTimestampError('');
          setAttrs({ quoteTimestamp: '' });
          return;
        }
        const seconds = parseTimestamp(text);
        if (seconds === null) {
          showTimestampError('Use minutes and seconds, like 32:14.');
          setAttrs({ quoteTimestamp: '' });
        } else if (capabilities.duration && seconds > capabilities.duration) {
          showTimestampError(`The episode is only ${formatTimestamp(capabilities.duration)} long.`);
          setAttrs({ quoteTimestamp: '' });
        } else {
          showTimestampError('');
          setAttrs({ quoteTimestamp: String(seconds) });
        }
      });

      // The timestamp section needs an episode to point at; without one the quote is a plain pull-quote.
      if (capabilities.episode) dom.querySelector('[data-timestamp-section]')?.classList.remove('hidden');
      else dom.querySelector('[data-no-episode]')?.classList.remove('hidden');

      // Suggested quotes: all show. "Use" fills the block and opens
      // the editor; "Another quote" opens it as is. The Done button needs some text.
      const ai = dom.querySelector('[data-ai]');
      const suggestions = Array.isArray(capabilities.suggestions) ? capabilities.suggestions : [];
      const hasText = () => Boolean((currentNode.attrs.quoteText || '').trim());
      const views = blockViews(dom, {
        canFinish: hasText,
        hasSuggestions: () => suggestions.length > 0,
        onChange: view => { if (view === 'preview') schedulePreview(); },
      });
      const rowTemplate = dom.querySelector('template[data-suggestion-row]');
      if (ai && rowTemplate && suggestions.length > 0) {
        const list = ai.querySelector('[data-suggestions]');

        suggestions.forEach(suggestion => {
          const row = rowTemplate.content.firstElementChild.cloneNode(true);
          // A missing start comes through as null, and Number(null) is 0 — that would read as "0:00".
          const seconds = suggestion.start == null ? NaN : Math.floor(Number(suggestion.start));
          const label = [suggestion.speaker, Number.isFinite(seconds) ? formatTimestamp(seconds) : null].filter(Boolean).join(' · ');
          row.querySelector('[data-suggestion-text]').textContent = `“${suggestion.text}”${label ? ` · ${label}` : ''}`;
          row.querySelector('[data-use]').addEventListener('click', () => {
            showTimestampError('');
            setAttrs({
              quoteText: suggestion.text || '',
              quoteSpeaker: suggestion.speaker || '',
              quoteTimestamp: capabilities.episode && Number.isFinite(seconds) ? String(seconds) : '',
            });
            showTimestampError('');
            views.showEditor({ focus: true });
          });
          list.appendChild(row);
        });
      }

      dom.querySelector('[data-remove]')?.addEventListener('mousedown', e => {
        e.preventDefault();
        const pos = typeof getPos === 'function' ? getPos() : null;
        if (pos === null || pos === undefined) return;
        const current = editor.state.doc.nodeAt(pos);
        if (current) editor.chain().focus().deleteRange({ from: pos, to: pos + current.nodeSize }).run();
      });

      // The preview is made of real links; never navigate away from the editor.
      preview?.addEventListener('click', e => e.preventDefault());

      syncFromNode();
      views.setView(initialView({ hasContent: hasText(), hasSuggestions: suggestions.length > 0 }));

      return {
        dom,
        // Typing in the fields must not reach ProseMirror (Backspace/Enter would act on the node).
        stopEvent: event => Boolean(event.target?.closest?.('input, textarea, button, a')),
        ignoreMutation: () => true,
        update(updatedNode) {
          if (updatedNode.type.name !== 'quote') return false;
          currentNode = updatedNode;
          syncFromNode();
          schedulePreview();
          return true;
        },
        destroy() {
          clearTimeout(previewTimer);
        },
      };
    };
  },

  addCommands() {
    return {
      insertQuote: (attrs = {}) => ({ chain }) =>
        chain().insertContent({ type: this.name, attrs }).run(),
    };
  },
});

// Offered whether or not the post has an episode: a typed quote is a fine pull-quote on its own, and the
// timestamp controls appear in the block only when the post has an episode.
export const quoteSlashItem = {
  title: 'Quote',
  description: 'A pull-quote, with a play-from button when it comes from the episode',
  icon: '❝',
  group: 'Blocks',
  keywords: ['quote', 'pull quote', 'pullquote', 'transcript', 'episode', 'speaker'],
  command: ({ editor, range }) => {
    editor.chain().focus().deleteRange(range).insertQuote().run();
  },
};
